/**
 * @file ProducerDetailPage.tsx
 * @description Карточка производителя и его сертификаты (phase08.5;
 * phase19.31 — разбор B: колонка до 960, сертификаты компактным списком).
 *
 * Шапка — в верхней панели: «назад» к производителям, название, состояние
 * пилюлей, видимая кнопка «Изменить» (SC67). Ниже — сведения (адрес, ИНН,
 * ОГРН, контакты) и «Сертификаты» строками: «СГР № … · кем выдан · до
 * 12.10.2027 · на сайте» с «⋯» (изменить, удалить с подтверждением).
 * Окно сертификата — `ProducerCertDialog`.
 *
 * phase19.16, партия «Юридические документы», шаг В (uiux-legal-steps-b-c
 * §6.2): блок сертификатов — `ProducerCertificatesList` (статус на сайте,
 * просроченные, файл, «где видят покупатели»).
 */

import { Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { ProducerEditDialog } from "../../../components/catalog/ProducerEditDialog";
import { ProducerCertDialog } from "../../../components/catalog/directory/ProducerCertDialog";
import { ProducerCertificatesList } from "../../../components/catalog/directory/ProducerCertificatesList";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { SectionCard } from "../../../components/ui/SectionCard";
import { StatusPill } from "../../../components/ui/StatusPill";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { catalogProducerCertificates, catalogProducers, type CatalogProducer, type ProducerCertificate } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { useHasPermission } from "../../../auth/hooks";
import { useOpenFileInTab } from "../../../hooks/useOpenFileInTab";
import { directoryActiveMeta } from "../../../lib/catalog-status";

export function ProducerDetailPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const notify = useNotify();
  const { confirm, request, settle } = useConfirm();
  const [producer, setProducer] = useState<CatalogProducer | null>(null);
  const [certs, setCerts] = useState<ProducerCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [certDialog, setCertDialog] = useState<{ cert: ProducerCertificate | null } | null>(null);
  const canEdit = useHasPermission("SHOP_MANAGE_CATALOG");
  const openInTab = useOpenFileInTab();
  const back = { to: `/shop/${slug}/catalog/producers`, label: "К производителям" };

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [p, c] = await Promise.all([catalogProducers.findOne(id), catalogProducerCertificates.list(id)]);
      setProducer(p);
      setCerts(c);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить производителя");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function removeCert(c: ProducerCertificate) {
    if (!id) return;
    const ok = await confirm({
      title: `Удалить сертификат № ${c.number}?`,
      description: "Файл тоже удалится. Документ пропадёт с карточки товара и со страницы «Сертификаты и декларации».",
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await catalogProducerCertificates.remove(id, c.id);
      notify.success("Сертификат удалён");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить сертификат");
    }
  }

  /** Файл сертификата — в новой вкладке (вкладка открывается синхронно по нажатию). */
  function openCertFile(c: ProducerCertificate) {
    if (id) openInTab(() => catalogProducerCertificates.downloadFile(id, c.id));
  }

  if (!producer) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Производитель" back={back} />
        {loading ? <ListSkeleton rows={4} compact /> : <ListError message={error ?? "Производитель не найден"} onRetry={() => void refresh()} />}
      </div>
    );
  }

  const details: Array<[string, string | null]> = [
    ["Юридический адрес", producer.legalAddress],
    ["ИНН", producer.inn],
    ["ОГРН", producer.ogrn],
    ["Телефон", producer.contactPhone],
    ["Почта", producer.contactEmail],
    ["Заметки", producer.notes],
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={producer.displayName}
        status={<StatusPill meta={directoryActiveMeta(producer)} />}
        back={back}
        secondaryActions={
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" aria-hidden />
            Изменить
          </Button>
        }
      />

      <div className="flex w-full max-w-[960px] flex-col gap-4">
        <SectionCard title="Сведения">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[180px_minmax(0,1fr)]">
            {details.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="type-caption text-on-surface-variant sm:pt-0.5">{label}</dt>
                <dd className="type-body -mt-2 break-words text-on-surface sm:mt-0">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>

        <ProducerCertificatesList
          certs={certs}
          producerActive={producer.isActive}
          canEdit={canEdit}
          onAdd={() => setCertDialog({ cert: null })}
          onEdit={(c) => setCertDialog({ cert: c })}
          onOpenFile={openCertFile}
          onRemove={(c) => void removeCert(c)}
        />
      </div>

      {editing ? (
        <ProducerEditDialog
          open
          producer={producer}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      ) : null}
      {certDialog && id ? (
        <ProducerCertDialog
          producerId={id}
          cert={certDialog.cert}
          onClose={() => setCertDialog(null)}
          onSaved={() => {
            setCertDialog(null);
            void refresh();
          }}
        />
      ) : null}
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
